/**
 * App.jsx — src/App.jsx
 *
 * Root component. Manages:
 *   - Login gate (shows LoginPage if no valid session token in localStorage)
 *   - Global theme (terminal / light / sage) — applied via CSS vars + localStorage
 *   - Global font scale (80%–130%) — applied via font-size on canvas + localStorage
 *   - Top-level tab routing: Dashboard | Earnings
 *   - Watchlist state (loaded from KV, persisted on change)
 *   - Quote polling (useQuotes hook, 30s interval, pauses when tab hidden)
 *   - Chart data for selected symbol (useChart hook)
 *
 * Component tree:
 *   App
 *   ├─ LoginPage              (shared) — shown when !loggedIn
 *   ├─ topbar
 *   │   ├─ tab buttons
 *   │   ├─ font scale (A- / A+)
 *   │   ├─ reset layout button
 *   │   ├─ theme swatches
 *   │   ├─ UsageBadge         (shared) — Haiku cost in SGD
 *   │   └─ Logout button
 *   ├─ Dashboard tab (canvas of draggable panels)
 *   │   ├─ Panel > DirectionGauge   (dashboard)
 *   │   ├─ Panel > DetailPanel      (dashboard)
 *   │   ├─ Panel > MAChart          (dashboard)
 *   │   ├─ Panel > PivotPoints      (dashboard)
 *   │   └─ Panel > Watchlist        (dashboard)
 *   └─ Earnings tab
 *       └─ EarningsDashboard        (earnings)
 *           ├─ EarningsTab
 *           └─ VolAnalysisTab
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Hooks
import { useQuotes }         from './hooks/useQuotes';
import { useChart }          from './hooks/useChart';

// Lib
import { getUserId, getWatchlist, putWatchlist, isLoggedIn, clearSession, getUsername, getRole, getModelPref, getToken, BASE, settingsGet } from './lib/api';
import { THEMES, applyTheme }                                             from './lib/theme';

// Shared components
import Panel      from './components/shared/Panel';
import LoginPage  from './components/shared/LoginPage';
import UsageBadge from './components/shared/UsageBadge';

// Dashboard components
import DirectionGauge from './components/dashboard/DirectionGauge';
import Watchlist      from './components/dashboard/Watchlist';
import DetailPanel    from './components/dashboard/DetailPanel';
import MAChart        from './components/dashboard/MAChart';
import PivotPoints    from './components/dashboard/PivotPoints';

// Earnings page

// Prospects page
import ProspectsPage from './components/prospects/ProspectsPage';

// Settings page
import SettingsPage from './components/shared/SettingsPage';

// Tooltips
import { PanelHelp } from './components/shared/IndicatorTooltip';

// Intraday chart
import IntradayChart from './components/dashboard/IntradayChart';
import IntradayPanel from './components/dashboard/IntradayPanel';
import TradeJournal from './components/journal/TradeJournal';
import AlertsPanel  from './components/alerts/AlertsPanel';
import SessionGuard from './components/SessionGuard';
import LiveMonitorPanel from './components/alerts/LiveMonitorPanel';
import { isMarketHours, useMarketOpen } from './lib/marketHours';
import { useAlertEngine } from './hooks/useAlertEngine';
import SessionNotesPanel, { SessionNotesBanner } from './components/dashboard/SessionNotes';
import StrategyPanel from './components/dashboard/StrategyPanel';

// ── Constants ─────────────────────────────────────────────────────────────────

// Always-fetched symbols (not user-editable)
const CORE = ['ES=F', '^VIX', 'SPY'];

// Default watchlist for new users
const DEFAULT_WL = ['SPY', 'QQQ', 'AAPL', 'NVDA', 'TSLA'];

// Font size steps applied to the canvas via font-size: Xem
const FONT_STEPS = [0.8, 0.9, 1.0, 1.1, 1.2, 1.3];

// Default positions and sizes for each draggable panel.
// Prominence order (left-to-right, top-to-bottom):
//   1. Strategy     — top-left corner (always visible, persistent)
//   2. Direction    — below strategy
//   3. Watchlist    — wide, centre-left
//   4. Chart        — top-right (large, defaults to SPY)
//   5. Session Plan — below watchlist
// Detail + Pivots spawn on demand when a watchlist item is clicked.
const DEFAULT_LAYOUT = {
  strategy: { pos: { x: 16,   y: 16  }, size: { w: 310, h: 340 } },
  signal:   { pos: { x: 16,   y: 372 }, size: { w: 310, h: 300 } },
  watchlist:{ pos: { x: 342,  y: 16  }, size: { w: 700, h: 340 } },
  chart:    { pos: { x: 1058, y: 16  }, size: { w: 560, h: 380 } },
  notes:    { pos: { x: 342,  y: 372 }, size: { w: 360, h: 300 } },
  monitor:  { pos: { x: 16,   y: 690 }, size: { w: 310, h: 360 } },
};

const LAYOUT_KEY = 'od:layout';

function loadLayout() {
  try {
    const raw = localStorage.getItem(LAYOUT_KEY);
    if (!raw) return DEFAULT_LAYOUT;
    const saved = JSON.parse(raw);
    // Merge with defaults so new panels added in future updates appear correctly
    const merged = { ...DEFAULT_LAYOUT };
    for (const key of Object.keys(DEFAULT_LAYOUT)) {
      if (saved[key]?.pos && saved[key]?.size) merged[key] = saved[key];
    }
    return merged;
  } catch { return DEFAULT_LAYOUT; }
}

function saveLayout(layout) {
  try { localStorage.setItem(LAYOUT_KEY, JSON.stringify(layout)); } catch {}
}

// ── Topbar icon button style (reused for A+/A-/Reset/Logout) ─────────────────
const iconBtn = {
  background: 'var(--bg-hover)', border: '1px solid var(--border)',
  color: 'var(--muted)', padding: '3px 6px', borderRadius: 4,
  fontSize: 11, fontWeight: 600, cursor: 'pointer', letterSpacing: '.04em',
};

// ── Root component ────────────────────────────────────────────────────────────
export default function App() {
  // ── Auth ──────────────────────────────────────────────────────────────────
  const [loggedIn, setLoggedIn] = useState(() => isLoggedIn());

  // ── Alert engine (runs on all tabs) ──────────────────────────────────────
  const alertEngine = useAlertEngine();
  const {
    engineOn: alertEngineOn, muted: alertMuted, liveState: alertLiveState,
    alertCount, tierAlertCounts, start: alertStart, stop: alertStop,
    mute: alertMute, unmute: alertUnmute,
    loadAll: alertLoadAll, injectMarketData,
  } = alertEngine;

  // Load alert settings once on login (loggedIn is declared above)
  useEffect(() => { if (loggedIn) alertLoadAll(); }, [loggedIn]);

  // Market hours — updates every 30s via shared hook
  const marketOpen = useMarketOpen();

  // Double-click to stop engine — track clicks on the header alert icon
  const alertIconClickRef = useRef({ count: 0, timer: null });
  const handleAlertIconClick = () => {
    const state = alertIconClickRef.current;
    state.count++;
    clearTimeout(state.timer);
    state.timer = setTimeout(() => { state.count = 0; }, 400);
    if (state.count === 1) {
      alertMuted ? alertUnmute() : alertMute();
    } else if (state.count >= 2) {
      state.count = 0;
      alertStop();
    }
  };

  // ── Persistent prefs (localStorage) ──────────────────────────────────────
  const [themeKey, setThemeKey] = useState(() => localStorage.getItem('od:theme')   || 'terminal');
  const [fontIdx,  setFontIdx]  = useState(() => Number(localStorage.getItem('od:fontIdx') || 2));

  // ── UI state ──────────────────────────────────────────────────────────────
  const [tab,         setTab]         = useState(() => localStorage.getItem('od:page') || 'dashboard');
  const [layout,      setLayout]      = useState(() => loadLayout());
  // Dynamic intraday panels: [{ id, symbol, pos, size }]
  const [intradayPanels, setIntradayPanels] = useState([]);
  // On-demand detail panels spawned by clicking a watchlist row: { sym, pos, size } | null
  const [detailPanel, setDetailPanel] = useState(null);
  const [selected,    setSelected]    = useState('SPY');
  const [diagResult,  setDiagResult]  = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [sessionNote, setSessionNote] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [sessionPlaceholder, setSessionPlaceholder] = useState('');

  // ── Watchlist ─────────────────────────────────────────────────────────────
  const [userId]               = useState(() => getUserId());
  const [watchlist, setWatchlist] = useState([]);
  const [wlLoaded,  setWlLoaded]  = useState(false);

  const t = THEMES[themeKey] || THEMES.terminal;

  // Apply theme CSS vars whenever theme changes
  useEffect(() => {
    applyTheme(t);
    localStorage.setItem('od:theme', themeKey);
  }, [themeKey, t]);

  useEffect(() => {
    localStorage.setItem('od:fontIdx', fontIdx);
  }, [fontIdx]);

  // Load watchlist from KV on login
  useEffect(() => {
    if (!loggedIn) return;
    getWatchlist(userId)
      .then(res => {
        const list = res.symbols.length ? res.symbols : DEFAULT_WL;
        setWatchlist(list);
        if (!list.includes(selected)) setSelected(list[0]);
      })
      .catch(() => setWatchlist(DEFAULT_WL))
      .finally(() => setWlLoaded(true));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn, userId]);

  // Load settings (session plan placeholder etc.) on login
  useEffect(() => {
    if (!loggedIn) return;
    settingsGet()
      .then(d => { if (d.sessionPlanPlaceholder != null) setSessionPlaceholder(d.sessionPlanPlaceholder); })
      .catch(() => {});
  }, [loggedIn]);

  // Persist watchlist to KV whenever it changes (after initial load)
  useEffect(() => {
    if (!wlLoaded || !loggedIn) return;
    putWatchlist(userId, watchlist).catch(console.error);
  }, [watchlist, userId, wlLoaded, loggedIn]);

  // ── Market data ───────────────────────────────────────────────────────────
  const allSymbols = useMemo(
    () => Array.from(new Set([...CORE, ...watchlist])),
    [watchlist],
  );

  // useQuotes polls every 30s; pauses when browser tab is hidden
  const { data, error, loading } = useQuotes(loggedIn ? allSymbols : [], 30000);

  const quotesBySym = useMemo(() => {
    const m = {};
    for (const q of data.quotes) m[q.symbol] = q;
    return m;
  }, [data]);

  // Chart + pivots data for currently selected symbol
  const { bars, loading: chartLoading, error: chartError } = useChart(loggedIn ? selected : null, '1y', '1d');
  const currentPrice = quotesBySym[selected]?.regularMarketPrice ?? quotesBySym[selected]?.preMarketPrice;

  // Inject VIX data into alert engine every time quotes refresh
  useEffect(() => {
    const vix = quotesBySym['^VIX'];
    if (!vix) return;
    injectMarketData({
      vixPrice:     vix.regularMarketPrice,
      vixPrevClose: vix.regularMarketPreviousClose ?? vix.regularMarketPrice,
    });
  }, [quotesBySym, injectMarketData]);

  // ── Watchlist actions ─────────────────────────────────────────────────────
  const addTicker = sym => {
    if (!watchlist.includes(sym)) { setWatchlist(p => [...p, sym]); setSelected(sym); }
  };
  const removeTicker = sym => {
    setWatchlist(p => p.filter(s => s !== sym));
    if (selected === sym) setSelected(watchlist.find(s => s !== sym) ?? '');
  };

  // ── Auth actions ──────────────────────────────────────────────────────────
  const logout = () => {
    clearSession();
    setLoggedIn(false);
    setWatchlist([]);
    setWlLoaded(false);
  };

  // ── Panel layout persistence ───────────────────────────────────────────────
  // Called by each named Panel when the user stops dragging or resizing.
  // Merges the changed panel into layout state and saves to localStorage.
  const handlePanelChange = useCallback((panelKey) => ({ pos, size }) => {
    setLayout(prev => {
      const next = { ...prev, [panelKey]: { pos, size } };
      saveLayout(next);
      return next;
    });
  }, []);

  const resetLayout = useCallback(() => {
    localStorage.removeItem(LAYOUT_KEY);
    setLayout(DEFAULT_LAYOUT);
  }, []);
  const openIntradayPanel = (sym) => {
    setIntradayPanels(prev => {
      // If already open, bring to front by bumping (no duplicate)
      if (prev.find(p => p.symbol === sym)) return prev;
      // Cascade position: offset each new panel
      const offset = prev.length * 30;
      return [...prev, {
        id:     `${sym}-${Date.now()}`,
        symbol: sym,
        pos:    { x: 50 + offset, y: 400 + offset },
        size:   { w: 680, h: 420 },
      }];
    });
  };

  const closeIntradayPanel = (id) => {
    setIntradayPanels(prev => prev.filter(p => p.id !== id));
  };

  // Open (or update) the on-demand detail+pivot panel for a symbol
  const openDetailPanel = (sym) => {
    setDetailPanel({ sym, pos: { x: 342, y: 372 }, size: { w: 380, h: 580 } });
  };

  // ── Diagnostics ──────────────────────────────────────────────────────────────
  const runDiag = async () => {
    setDiagLoading(true); setDiagResult(null);
    try {
      const r = await fetch(`${BASE}/diag?symbol=SPY`, {
        headers: { 'x-session-token': getToken() }
      });
      const d = await r.json();
      setDiagResult(d);
    } catch(e) { setDiagResult({ error: e.message }); }
    finally { setDiagLoading(false); }
  };

  // ── Topbar status ─────────────────────────────────────────────────────────
  const lastUpdate = data.fetchedAt
    ? new Date(data.fetchedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : null;
  const scale = FONT_STEPS[fontIdx] || 1.0;

  // ── Login gate ────────────────────────────────────────────────────────────
  if (!loggedIn) {
    return <LoginPage onLogin={() => setLoggedIn(true)} />;
  }

  return (
    <div style={{
      height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
      background: 'var(--bg)', color: 'var(--text)',
      fontFamily: "var(--sans, 'Inter', system-ui, sans-serif)", fontSize: 14,
    }}>

      {/* ── Topbar ──────────────────────────────────────────────────────── */}
      <header className={`topbar${alertEngineOn ? ' topbar-alert-active' : ''}`}>
        {/* Alert glow overlay — animated border when engine running */}
        {alertEngineOn && (
          <div className={`topbar-alert-glow${alertMuted ? ' muted' : ''}`} />
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', minWidth: 0 }}>
          {/* Logo */}
          <div className="topbar-title" style={{ flexShrink: 0 }}>
            <span className="accent">▸</span>OPTIONS<span className="accent">_</span>DESK
          </div>

          {/* Main tab nav */}
          <div className="topbar-tabs" style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', gap: 1, background: 'var(--bg)', borderRadius: 5, padding: 2 }}>
              {[['dashboard', '⬛ Dashboard'], ['prospects', '⟺ Prospects'], ['journal', '📒 Journal'], ['alerts', '🔔 Alerts'], ['settings', '⚙ Settings']].map(([key, label]) => (
                <button key={key} onClick={() => { setTab(key); localStorage.setItem('od:page', key); }} style={{
                  padding: '4px 10px', borderRadius: 4, border: 'none', cursor: 'pointer',
                  fontSize: 11, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase',
                  background: tab === key ? 'var(--accent)' : 'transparent',
                  color:      tab === key ? 'var(--bg)'     : 'var(--muted)',
                  transition: 'all .15s', whiteSpace: 'nowrap',
                }}>{label}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="topbar-meta">
          {/* Data freshness — dot + time, error on hover */}
          {error ? (
            <span style={{ color: 'var(--down)', cursor: 'pointer' }} onClick={runDiag} title={error}>⚠</span>
          ) : (
            <span title={data.cached ? `Cached · ${lastUpdate}` : `Live · ${lastUpdate}`} style={{ whiteSpace: 'nowrap' }}>
              <span className={`dot ${loading ? '' : data.cached ? 'cached' : 'live'}`} />
              {loading ? '…' : lastUpdate || ''}
            </span>
          )}
          {diagLoading && <span style={{ color: 'var(--warn)' }}>⟳</span>}

          {/* Font scale */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <button onClick={() => setFontIdx(i => Math.max(0, i - 1))} style={iconBtn} title="Smaller text">A−</button>
            <span style={{ fontFamily: 'monospace', fontSize: 10, color: 'var(--faint)', minWidth: 26, textAlign: 'center' }}>{Math.round(scale * 100)}%</span>
            <button onClick={() => setFontIdx(i => Math.min(FONT_STEPS.length - 1, i + 1))} style={iconBtn} title="Larger text">A+</button>
          </div>

          {/* Reset layout — icon only, dashboard tab only */}
          {tab === 'dashboard' && (
            <button onClick={resetLayout} style={iconBtn} title="Reset panel positions to defaults">⊞</button>
          )}

          {/* Theme swatches — no label */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            {Object.entries(THEMES).map(([key, theme]) => (
              <button key={key} onClick={() => setThemeKey(key)} title={theme.name}
                style={{
                  width: 18, height: 18, borderRadius: 3, cursor: 'pointer', padding: 0,
                  border: themeKey === key ? '2px solid var(--accent)' : '1.5px solid var(--border)',
                  background: theme.swatch1, position: 'relative', overflow: 'hidden',
                  boxShadow: themeKey === key ? '0 0 0 1px var(--accent)' : 'none',
                }}
              >
                <div style={{ position: 'absolute', bottom: 0, right: 0, width: 8, height: 8, background: theme.swatch2 }} />
              </button>
            ))}
          </div>

          {/* Alert engine icon — always visible, dimmed outside market hours */}
          <button
            onClick={alertEngineOn ? handleAlertIconClick : alertStart}
            title={
              !alertEngineOn
                ? `Start alert engine${!marketOpen ? ' (market currently closed)' : ''}`
                : alertMuted
                ? 'Muted — click to unmute · double-click to stop'
                : 'Alerts active — click to mute · double-click to stop'
            }
            style={{
              background: 'none', border: '1px solid',
              borderColor: alertEngineOn
                ? (alertMuted ? 'var(--warn)' : 'var(--up)')
                : marketOpen ? 'var(--border)' : 'transparent',
              borderRadius: 4, cursor: 'pointer', padding: '2px 5px',
              fontSize: 13, lineHeight: 1,
              color: alertEngineOn
                ? (alertMuted ? 'var(--warn)' : 'var(--up)')
                : marketOpen ? 'var(--muted)' : 'var(--faint)',
              opacity: !alertEngineOn && !marketOpen ? 0.5 : 1,
              position: 'relative',
            }}
          >
            {alertEngineOn ? (alertMuted ? '🔇' : '🔔') : '🔕'}
            {alertEngineOn && alertCount > 0 && (
              <span style={{
                position: 'absolute', top: -5, right: -5,
                background: 'var(--down)', color: '#fff',
                fontSize: 8, fontWeight: 700, borderRadius: 10,
                padding: '1px 3px', lineHeight: 1.4, fontFamily: 'monospace',
              }}>{alertCount > 99 ? '99+' : alertCount}</span>
            )}
          </button>

          {/* API cost */}
          <UsageBadge />

          {/* Logout — icon only */}
          <button onClick={logout} style={{ ...iconBtn, color: 'var(--down)', borderColor: 'var(--down-soft)' }} title="Sign out">⏏</button>
        </div>
      </header>

      {/* ── Session Notes Banner ─────────────────────────────────────────── */}
      {sessionNote && !bannerDismissed && (
        <SessionNotesBanner
          note={sessionNote}
          onEdit={() => { setTab('dashboard'); setBannerDismissed(false); }}
          onDismiss={() => setBannerDismissed(true)}
        />
      )}

      {/* ── Diagnostic result banner ─────────────────────────────────────── */}
      {diagResult && (
        <div style={{
          background: diagResult.results?.query1?.hasData || diagResult.results?.query2?.hasData
            ? 'var(--up-soft)' : 'var(--down-soft)',
          border: `1px solid ${diagResult.results?.query1?.hasData || diagResult.results?.query2?.hasData ? 'var(--up)' : 'var(--down)'}`,
          padding: '10px 20px', fontSize: 12, fontFamily: 'monospace', lineHeight: 1.7,
          display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start',
        }}>
          <div>
            <strong style={{ color: 'var(--text)' }}>Diagnostics · {diagResult.ts}</strong>
            <div>Market window: {diagResult.marketWindow ? 'YES' : 'NO (outside 04:00–20:00 ET)'}</div>
            <div style={{ color: diagResult.results?.query1?.hasData ? 'var(--up)' : 'var(--down)' }}>
              query1: {diagResult.results?.query1?.hasData ? '✓ data OK' : `✗ ${diagResult.results?.query1?.status} — ${diagResult.results?.query1?.bodySnippet?.slice(0,80) || diagResult.results?.query1?.error}`}
            </div>
            <div style={{ color: diagResult.results?.query2?.hasData ? 'var(--up)' : 'var(--down)' }}>
              query2: {diagResult.results?.query2?.hasData ? '✓ data OK' : `✗ ${diagResult.results?.query2?.status} — ${diagResult.results?.query2?.bodySnippet?.slice(0,80) || diagResult.results?.query2?.error}`}
            </div>
            <div>KV: {diagResult.results?.kv?.ok ? '✓ OK' : `✗ ${diagResult.results?.kv?.error}`}</div>
            <div style={{ marginTop: 4, color: 'var(--warn)', fontWeight: 700 }}>→ {diagResult.recommendation}</div>
          </div>
          <button onClick={() => setDiagResult(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--faint)', fontSize: 18, alignSelf: 'flex-start' }}>×</button>
        </div>
      )}

      {/* ── Dashboard tab ────────────────────────────────────────────────── */}
      {tab === 'dashboard' && (
        <div className="canvas" style={{ fontSize: `${scale}em` }}>

          {/* 1. Strategy — top-left, persistent, always visible */}
          <Panel title="Strategy" icon="📐" initialPos={layout.strategy.pos} initialSize={layout.strategy.size} onChangeEnd={handlePanelChange('strategy')}>
            <StrategyPanel />
          </Panel>

          {/* 2. Open Direction Signal — below strategy */}
          <Panel title={<PanelHelp title="Open Direction Signal" help="Composite of ES futures, VIX, and SPY pre-market. Scores -100 (bearish) to +100 (bullish). Hover the VIX reading for breakdown." />} icon="◈" initialPos={layout.signal.pos} initialSize={layout.signal.size} onChangeEnd={handlePanelChange('signal')}>
            <DirectionGauge
              esQuote={quotesBySym['ES=F']}
              vixQuote={quotesBySym['^VIX']}
              spyQuote={quotesBySym['SPY']}
            />
          </Panel>

          {/* 3. Watchlist — centre-left, wide */}
          <Panel title="Watchlist" icon="≡" initialPos={layout.watchlist.pos} initialSize={layout.watchlist.size} onChangeEnd={handlePanelChange('watchlist')}>
            <Watchlist
              symbols={watchlist}
              quotesBySym={quotesBySym}
              onAdd={addTicker}
              onRemove={removeTicker}
              onSelect={setSelected}
              onReorder={setWatchlist}
              onOpenChart={openIntradayPanel}
              onOpenDetail={openDetailPanel}
              selected={selected}
            />
          </Panel>

          {/* 4. Chart — top-right, defaults to SPY */}
          <Panel title={<PanelHelp title={`Chart · SPY · 50/200 SMA`} help="50-day MA (blue) = short-term trend. 200-day MA (yellow) = long-term trend. Price above both = bull regime. Hover the MA labels for signal." />} icon="◎" initialPos={layout.chart.pos} initialSize={layout.chart.size} onChangeEnd={handlePanelChange('chart')}>
            {chartError    ? <div className="err-msg">⚠ {chartError}</div>
              : chartLoading ? <div className="empty">Loading chart…</div>
              : <MAChart bars={bars} symbol={selected} currentPrice={currentPrice} />
            }
          </Panel>

          {/* 5. Session Plan — below watchlist */}
          <Panel title="Session Plan" icon="📋" initialPos={layout.notes.pos} initialSize={layout.notes.size} onChangeEnd={handlePanelChange('notes')}>
            <SessionNotesPanel
              placeholder={sessionPlaceholder}
              onNoteChange={(note) => {
                setSessionNote(note);
                if (note) setBannerDismissed(false);
              }}
            />
          </Panel>

          {/* 6. Live Monitor — SPY alert engine status */}
          <Panel
            title="Live Monitor · SPY"
            icon="◈"
            initialPos={layout.monitor.pos}
            initialSize={layout.monitor.size}
            onChangeEnd={handlePanelChange('monitor')}
          >
            <LiveMonitorPanel
              engineOn={alertEngineOn}
              muted={alertMuted}
              liveState={alertLiveState}
              alertCount={alertCount}
              onStart={alertStart}
              onGoToAlerts={() => setTab('alerts')}
              vixQuote={quotesBySym['^VIX']}
            />
          </Panel>

          {/* ── On-demand Detail + Pivot panels (spawned when watchlist row clicked) ── */}
          {detailPanel && (
            <>
              <Panel
                title={`Detail · ${detailPanel.sym}`}
                icon="◉"
                initialPos={detailPanel.pos}
                initialSize={{ w: 380, h: 340 }}
                onClose={() => setDetailPanel(null)}
              >
                <DetailPanel symbol={detailPanel.sym} quote={quotesBySym[detailPanel.sym]} />
              </Panel>
              <Panel
                title={<PanelHelp title={`Pivots · ${detailPanel.sym}`} help="Floor pivot points from previous day H/L/C. P = equilibrium. R1/R2/R3 = resistance. S1/S2/S3 = support. Scalpers use these as entry/exit zones." />}
                icon="◇"
                initialPos={{ x: detailPanel.pos.x + 396, y: detailPanel.pos.y }}
                initialSize={{ w: 280, h: 300 }}
                onClose={() => setDetailPanel(null)}
              >
                {chartLoading
                  ? <div className="empty">Loading…</div>
                  : <PivotPoints bars={bars} currentPrice={quotesBySym[detailPanel.sym]?.regularMarketPrice ?? currentPrice} symbol={detailPanel.sym} />
                }
              </Panel>
            </>
          )}

          {/* ── Dynamic intraday panels (spawned per symbol from watchlist ⌘ button) ── */}
          {intradayPanels.map(p => (
            <IntradayPanel
              key={p.id}
              id={p.id}
              symbol={p.symbol}
              quote={quotesBySym[p.symbol]}
              initialPos={p.pos}
              initialSize={p.size}
              onClose={closeIntradayPanel}
            />
          ))}
        </div>
      )}

      {/* ── Prospects tab ────────────────────────────────────────────────── */}
      {/* Always mounted so state/data survives tab switches */}
      <div style={{ flex: 1, overflow: 'auto', fontSize: `${scale}em`, display: tab === 'prospects' ? 'block' : 'none' }}>
        <ProspectsPage t={t} />
      </div>

      {/* ── Settings tab ─────────────────────────────────────────────────── */}
      {tab === 'settings' && (
        <div style={{ flex: 1, overflow: 'auto', fontSize: `${scale}em` }}>
          <SettingsPage
            watchlist={watchlist}
            username={getUsername()}
            onUpdateWatchlist={(next) => { setWatchlist(next); }}
            onSettingsChange={(patch) => {
              if (patch.sessionPlanPlaceholder != null) setSessionPlaceholder(patch.sessionPlanPlaceholder);
            }}
          />
        </div>
      )}

      {/* ── Journal tab ──────────────────────────────────────────────────── */}
      {tab === 'journal' && (
        <div style={{ flex: 1, overflow: 'hidden', fontSize: `${scale}em` }}>
          <TradeJournal />
        </div>
      )}

      {/* ── Alerts tab ───────────────────────────────────────────────────── */}
      {tab === 'alerts' && (
        <div style={{ flex: 1, overflow: 'auto', fontSize: `${scale}em` }}>
          <AlertsPanel {...alertEngine} vixQuote={quotesBySym['^VIX']} />
        </div>
      )}

      <footer className="disclaimer">
        DATA VIA YAHOO FINANCE · PRE-MARKET BID/ASK MAY BE STALE · FOR INFORMATIONAL USE ONLY · NOT INVESTMENT ADVICE
      </footer>

      {/* ── Session Guard: plan reminder + warning time block ─────────── */}
      {loggedIn && (
        <SessionGuard
          engineOn={alertEngineOn}
          stopEngine={alertStop}
          marketOpen={marketOpen}
          globalSettings={alertEngine.globalSettings}
        />
      )}
    </div>
  );
}
